'use client';

import { useEffect, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMd } from '@/components/workspace/panels/panel-shell';
import { useHandsetLandscape } from '@/hooks/use-handset-landscape';
import {
  useOverviewMode,
  usePresentedWindowId,
  useWorkspaceActions,
  useWorkspaceItems,
} from '@/components/workspace/store';
import {
  uniqueParticipants,
  useParticipantsMirror,
} from '@/components/workspace/presence/presence-bridge';
import { orderScreens } from '@/lib/workspaces/screens';
import { identityVisual } from '@/components/workspace/rail/rail-dock';
import {
  UNNAMED_ITEM_LABEL,
  resolveItemLabel,
  useLiveContentMeta,
} from '@/components/workspace/item-label';
import { Avatar } from '@/components/avatar';
import { announceWindowClosed } from '@/components/workspace/closed-window-undo';
import type { WorkspaceItemDTO } from '@/lib/workspaces/types';
import { StageCorners } from './stage-corners';

/**
 * Stage ON A PHONE: THE LIST OF OPEN APPS (2026-10-03).
 *
 * WHY IT IS A LIST. Stage on a desktop is a camera state over world
 * geometry, and at phone widths no zoom level of that world is legible, so
 * below md Stage has always been a plain sheet (decision S1). Until today the
 * sheet drew the desktop's model in small: a reel of app icons, then a grid
 * of SCREEN cards, each a minimap of the windows on that screen, and a "New
 * screen" tile. The owner, looking at it on a phone: "should we just list all
 * open apps in the stage view? each app opens in fill-screen of full-screen
 * mode - we cannot really work with desktop windows on mobile, so the screens
 * are not really meaningful but rather confusing, eg Docs + Revent is very
 * confusing on mobile". A phone presents ONE app at a time, filling the tab
 * (lib/workspaces/handset-present.ts), so a card named after two apps
 * promised an arrangement the phone never shows, and the same apps appeared
 * twice, once in the reel and once inside a card. Three layouts were drawn
 * and the owner picked the list ("do A").
 *
 * WHAT IT SHOWS. Every open app once, by name, in the order the desktop's
 * taskbar has them (`orderScreens`): its mark, its name, who else is in it,
 * a dot on the one the phone is showing, and a close button. A tap opens the
 * app filling the screen, through the same `ws:screen {kind:'item'}` the
 * desktop rail uses (which leaves Stage and restores a minimized app). An
 * app minimized on a desktop is simply an open app here: a phone has no
 * minimized state to show. Close removes the window and offers Undo above
 * the navigation, the house pattern for a reversible act
 * (closed-window-undo.tsx); closing keeps the app's content.
 *
 * WHAT IS GONE FROM THE PHONE, AND ONLY FROM THE PHONE. Screens: the cards,
 * their minimaps, the "New screen" tile, and the one-time "Remove N empty
 * screens" offer (an offer about things this sheet no longer draws). Nothing
 * is deleted or rearranged: screens are shared, they are all still there on
 * a desktop, and this sheet writes nothing but what a tap asks for. A screen
 * that holds only notes is not reachable from a phone's Stage any more; it
 * was shown shrunk to fit, with the notes too small to read.
 *
 * The canvas still runs its overview machinery underneath (harmless,
 * covered); StageCorners mounts HERE below md so the workspace switcher,
 * Share and presence stay reachable on the sheet.
 */
export function StageSheet() {
  const t = useT();
  const overviewMode = useOverviewMode();
  const isMd = useIsMd();
  const landscape = useHandsetLandscape();
  // Same SSR gate as the bar: `useIsMd` starts false, so ungated the sheet
  // would flash on desktop during hydration whenever overview restores.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const items = useWorkspaceItems();
  // Every name on this sheet resolves live-first through item-label.ts, the
  // same rule as the rail, so a renamed app never reads one way here and
  // another there (the 2026-09-13 browser pass). Read-only: nothing is
  // written back.
  const live = useLiveContentMeta();
  // The app the phone is showing: the parked screen's own window, or the top
  // window of a screen that has several. The reel this list replaces marked
  // the parked SCREEN, which on a screen with two apps marked nothing.
  const presentedId = usePresentedWindowId();
  const participants = uniqueParticipants(useParticipantsMirror());
  const { removeItem } = useWorkspaceActions();

  if (!mounted || (isMd && !landscape) || !overviewMode) return null;

  // Sheets are screens, not apps: the list is the windows.
  const apps = orderScreens(items).filter((it) => it.type !== 'board');
  const go = (id: string) =>
    window.dispatchEvent(
      new CustomEvent('ws:screen', { detail: { kind: 'item', itemId: id } }),
    );
  const close = (item: WorkspaceItemDTO) => {
    // Say what is about to go BEFORE it goes, with the whole item, so the
    // close toast can put it back as it was.
    announceWindowClosed(item);
    void removeItem(item.id);
  };

  return (
    <div
      data-testid="workspace-stage-sheet"
      className="pointer-events-auto absolute inset-0 z-[90] flex flex-col bg-background"
    >
      {/* The corner chips (switcher top-left, Share + presence top-right)
          anchor to the sheet, offset past the bar's edge in landscape. */}
      {/* pointer-events-none: this overlay spans the WHOLE sheet just to
          anchor the corner chips; with default pointer-events it ate the
          taps for everything underneath it. The chips re-enable interaction
          on themselves. */}
      <div
        className="pointer-events-none absolute inset-y-0 right-0"
        style={{ left: 'var(--ws-bar-l, 0px)' }}
      >
        <StageCorners />
      </div>
      {/* THE CHIPS HAVE THEIR OWN BAND (the visual pass of 2026-10-03). The
          list used to start under 64 px of padding INSIDE the scroller, so
          with eleven apps open the rows scrolled up behind the two chips:
          a mark behind the workspace name, a close button under Share. The
          band is outside the scroller now, so a row leaves the page at the
          band's lower edge and never passes behind a chip. */}
      <div aria-hidden className="h-14 shrink-0" />
      <div
        data-testid="stage-apps-scroller"
        className="min-h-0 flex-1 overflow-y-auto px-4 pt-2 pb-[calc(var(--ws-bar-b,0px)+env(safe-area-inset-bottom,0px)+1rem)]"
        style={{
          paddingBottom:
            'calc(var(--ws-bar-b, 0px) + env(safe-area-inset-bottom) + 1rem)',
          paddingLeft: 'calc(var(--ws-bar-l, 0px) + 1rem)',
        }}
      >
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t('workspace.rail.openApps')}
        </div>
        {apps.length === 0 ? (
          <p data-testid="stage-apps-empty" className="py-10 text-center text-sm text-muted-foreground">
            {t('workspace.panels.nothingOpenYet')}
          </p>
        ) : (
          <ul role="list" data-testid="stage-apps" className="divide-y divide-border/60">
            {apps.map((item) => {
              const name = resolveItemLabel(item, live, UNNAMED_ITEM_LABEL);
              const current = item.id === presentedId;
              const peers = uniqueParticipants(
                participants.filter((p) => p.screenItemId === item.id),
              );
              return (
                <li key={item.id} className="flex items-center gap-1">
                  {/* The row IS the app: one target as tall as a thumb, named
                      by the app's name alone (the mark is decoration, and its
                      initial letter must not be read out before the name). */}
                  <button
                    type="button"
                    data-testid="stage-app-row"
                    onClick={() => go(item.id)}
                    aria-current={current ? 'true' : undefined}
                    // -ml-2 pl-2: the pressed ground reaches 8 px past the
                    // mark instead of ending flush against it.
                    className="-ml-2 flex min-h-14 min-w-0 flex-1 items-center gap-3 rounded-xl py-2 pl-2 pr-1 text-left transition-colors active:bg-accent/60"
                  >
                    <span aria-hidden className="flex h-9 w-9 shrink-0 items-center justify-center">
                      {identityVisual(item, 36, live)}
                    </span>
                    {/* The dot sits against the name it marks. It was drawn at
                        the row's far end, 4 px from the close button, where it
                        read as part of that control (the visual pass). */}
                    <span className="flex min-w-0 flex-1 items-center gap-2">
                      <span
                        className={cn(
                          'min-w-0 truncate text-[15px] text-foreground',
                          current && 'font-medium',
                        )}
                      >
                        {name}
                      </span>
                      {current && (
                        <span
                          aria-hidden
                          data-testid="stage-app-current"
                          className="h-1.5 w-1.5 shrink-0 rounded-full bg-foreground"
                        />
                      )}
                    </span>
                    {peers.length > 0 && (
                      <span aria-hidden data-testid="stage-app-peers" className="flex shrink-0 -space-x-1">
                        {peers.slice(0, peers.length > 3 ? 2 : 3).map((p) => (
                          <span
                            key={p.user.peerId}
                            className="inline-flex h-5 w-5 overflow-hidden rounded-full"
                            style={{ boxShadow: '0 0 0 2px var(--color-background)' }}
                          >
                            <Avatar
                              seed={p.user.username || p.user.peerId}
                              type="human"
                              src={p.user.avatarUrl ?? null}
                              className="h-full w-full"
                            />
                          </span>
                        ))}
                        {peers.length > 3 && (
                          <span
                            className="flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-semibold leading-none text-muted-foreground"
                            style={{ boxShadow: '0 0 0 2px var(--color-background)' }}
                          >
                            +{peers.length - 2}
                          </span>
                        )}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    data-testid="stage-app-close"
                    onClick={() => close(item)}
                    aria-label={t('stage.closeApp', { name })}
                    // -mr-3.5: the 16 px glyph is centred in a 44 px target,
                    // so 14 px of margin puts the glyph's right edge on the
                    // hairline's, and the target reaches into the page margin.
                    className="-mr-3.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors active:bg-accent/60 active:text-foreground"
                  >
                    <X className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
